// ============================================================
// EffectItemBench - one Always on item or one Solo mode on a bench of its
// own (plan modes-rework, stage 4): its name; for Always on, what triggers
// it (or nothing - always up); for a Solo mode, the one-at-a-time group it
// belongs to, typed by name; then its effects as cards (`EffectList`).
// Save hands back the bundle as the sheet's lists read it, and waits for a
// name; Cancel leaves it as it was (Escape asks first when the draft
// differs, by value).
//
// The library opens it on an asset, whose name IS the bundle's (`title`:
// renamed in the library, never here, so the two cannot disagree).
// ============================================================
import { useState } from 'react';
import { OWN_STATE_CHOICES, type OwnState } from '../../engine/character/effectWhen';
import { bundleOf, withGroupNamed, withTrigger } from '../../engine/character/bundleEdits';
import type { EffectItem, SoloMode } from '../../engine/character/soloModes';
import common from '../../styles/common.module.css';
import bench from '../../styles/WeaponBench.module.css';
import styles from '../../styles/Stances.module.css';
import useLeave from '../weaponEditor/useLeave';
import { stancePreview } from '../../engine/roster/stancePreview';
import type { Character } from '../../engine/character/types';
import type { CombatState } from '../../engine/roster/types';
import BenchTableBar, { BenchReturn } from '../weaponEditor/BenchTableBar';
import useFocusTrap from '../../hooks/useFocusTrap.js';
import clsx from '../../utils/clsx';
import EffectList from '../stances/EffectList';

type BundleKind = 'alwaysOn' | 'soloMode';
type Bundle = EffectItem | SoloMode;

interface Props {
  kind: BundleKind;
  bundle: Bundle;
  /** Where it is: "My library · Old hatred v2", "Brother Vael · sheet". */
  where: string;
  onSave: (bundle: Bundle) => void;
  onClose: () => void;
  locked?: boolean;
  /** The name it carries, shown and not edited (a library asset's own); absent, the bench asks for one. */
  title?: string;
  /** Opened from a sheet in play: "Back to the table" steps the bench aside and keeps the draft. */
  atTable?: boolean;
  /** The sheet it is on (and its live state): the bench then shows what the bundle changes on it. */
  on?: { sheet: Character; state?: CombatState | null };
}

const TITLES: Record<BundleKind, string> = { alwaysOn: 'Always on', soloMode: 'Solo mode' };

/** Always on: up always, or while a state is carried. */
function Trigger({ item, set, locked }: { item: EffectItem; set: (b: Bundle) => void; locked: boolean }) {
  return (
    <label className={common.label}>
      Up{' '}
      <select
        className={common.input}
        data-testid="bundle-trigger"
        value={item.whileOwn ?? ''}
        disabled={locked}
        onChange={(e) => set(withTrigger(item, (e.target.value || null) as OwnState | null))}
      >
        <option value="">always</option>
        {OWN_STATE_CHOICES.map((s) => (
          <option key={s.id} value={s.id}>
            while I am {s.label}
          </option>
        ))}
      </select>
    </label>
  );
}

/**
 * A Solo mode: the one-at-a-time group it belongs to, by name (none: switched freely). The text is its own state, so
 * a space can be typed; the draft follows each key, worked out from the mode AS OPENED - the same name keeps its group.
 */
function Group({
  opened,
  set,
  locked,
}: {
  opened: SoloMode;
  set: (f: (b: Bundle) => Bundle) => void;
  locked: boolean;
}) {
  const [text, setText] = useState(opened.group?.name ?? '');
  const regroup = (value: string) => {
    setText(value);
    const { group } = withGroupNamed(opened, value);
    set(({ group: _was, ...d }: SoloMode) => (group ? { ...d, group } : d));
  };
  return (
    <label className={common.label}>
      One at a time with{' '}
      <input
        className={common.input}
        data-testid="bundle-group"
        placeholder="no group - switched freely"
        value={text}
        maxLength={40}
        disabled={locked}
        onChange={(e) => regroup(e.target.value)}
      />
    </label>
  );
}

/** What the bundle changes on the sheet itself, worked out by the effective sheet (`stancePreview`). */
function OnTheSheet({ on, draft }: { on: NonNullable<Props['on']>; draft: Bundle }) {
  const lines = stancePreview(
    { sheet: on.sheet, state: (on.state ?? {}) as CombatState },
    draft.effects,
    draft.name,
  ).sheet;
  return (
    <section className={styles.pinBox} aria-label="On the sheet" data-testid="stance-on-sheet">
      <h4 className={styles.pinHead}>On the sheet</h4>
      {lines.length ? (
        <ul className={styles.pinList}>
          {lines.map((line, i) => (
            // By place: two lines may read the same ("Gains Frenzy" from two effects).
            <li key={i}>{line}</li>
          ))}
        </ul>
      ) : (
        <p className={styles.says}>Nothing on the sheet itself: it changes the attack, the defence or the turn.</p>
      )}
    </section>
  );
}

/** The header: what it is, its name, where, Save (waiting for a name) and Cancel. */
function Head(p: Props & { draft: Bundle; set: (b: Bundle) => void }) {
  const { kind, where, locked, onSave, onClose, draft, set, title } = p;
  const named = !!draft.name.trim();
  const save = () => {
    const saved = bundleOf(kind, draft);
    if (saved) onSave(saved);
  };
  return (
    <header className={bench.head}>
      <div className={bench.headWho}>
        <span className={bench.caps}>{TITLES[kind]}</span>
        {title !== undefined ? (
          <h1 className={bench.title} data-testid="bundle-name">
            {title}
          </h1>
        ) : (
          <input
            className={common.input}
            aria-label={`${TITLES[kind]} name`}
            data-testid="bundle-name"
            value={draft.name}
            maxLength={40}
            disabled={locked}
            onChange={(e) => set({ ...draft, name: e.target.value })}
          />
        )}
        <span className={bench.where}>{where}</span>
      </div>
      <div className={bench.headActions}>
        {!locked && (
          <button
            type="button"
            className={bench.primary}
            data-testid="bundle-save"
            disabled={!named}
            title={named ? undefined : 'It needs a name.'}
            onClick={save}
          >
            Save
          </button>
        )}
        <button type="button" className={bench.secondary} onClick={onClose}>
          {locked ? 'Close' : 'Cancel'}
        </button>
      </div>
    </header>
  );
}

/** Escape on a change asks first: Discard closes, Keep editing goes back. */
function Discard({ name, onDiscard, onKeep }: { name: string; onDiscard: () => void; onKeep: () => void }) {
  return (
    <div className={bench.confirm} role="alertdialog" aria-label="Discard the changes?">
      <span>Discard the changes to {name}?</span>
      <button type="button" className={bench.danger} onClick={onDiscard}>
        Discard
      </button>
      <button type="button" className={bench.secondary} onClick={onKeep}>
        Keep editing
      </button>
    </div>
  );
}

export default function EffectItemBench(props: Props) {
  const { kind, onClose, locked = false, title } = props;
  // A library bundle carries its asset's name: what the card says is what a sheet's copy will say.
  const [bundle] = useState<Bundle>(() => (title !== undefined ? { ...props.bundle, name: title } : props.bundle));
  const [draft, set] = useState<Bundle>(bundle);
  const [away, setAway] = useState(false);
  const trapRef = useFocusTrap(!away);
  const leave = useLeave(!locked && !away, JSON.stringify(draft) !== JSON.stringify(bundle), onClose);
  return (
    <>
      <div
        ref={trapRef}
        className={clsx(bench.layer, away && bench.away)}
        role="dialog"
        aria-modal="true"
        aria-label={`Edit ${TITLES[kind]}`}
        data-testid="bundle-bench"
      >
        {props.atTable && (
          <BenchTableBar facts={{ round: null, acting: null, owed: null }} onBack={() => setAway(true)} />
        )}
        <Head {...props} locked={locked} draft={draft} set={set} />
        {leave.asking && <Discard name={draft.name} onDiscard={onClose} onKeep={() => leave.setAsking(false)} />}
        <fieldset className={bench.body} disabled={locked}>
          {props.on && <OnTheSheet on={props.on} draft={draft} />}
          <div className={styles.row}>
            {kind === 'alwaysOn' ? (
              <Trigger item={draft} set={set} locked={locked} />
            ) : (
              <Group opened={bundle as SoloMode} set={set as (f: (b: Bundle) => Bundle) => void} locked={locked} />
            )}
          </div>
          <EffectList
            effects={draft.effects}
            onChange={(effects) => set({ ...draft, effects })}
            id={draft.id}
            owner={draft.name || TITLES[kind]}
            locked={locked}
          />
        </fieldset>
      </div>
      {away && <BenchReturn name={draft.name || TITLES[kind]} onReturn={() => setAway(false)} />}
    </>
  );
}
